/**
 * The team calendar, in four views of one fetch: DAY, WEEK, MONTH and the RESOURCE GRID. Day and
 * Week (PR 11) read the same /api/calendar months as Month, a week across two months reading both,
 * so no view learns anything another cannot: the label is the API's, already masked.
 *
 * MONTH answers "what is happening" — a traditional calendar, weeks as rows,
 * each day carrying only what is actually booked. It is the view most people
 * read without being taught anything.
 *
 * RESOURCE GRID answers the opposite question, "who is free" — one row per
 * person, one cell per day, ported from prototype/scheduler.html which is the
 * tested behaviour. An empty row is a real answer there, which is exactly why
 * it is the wrong shape for the first question and the right one for planning.
 *
 * Both read the same payload and the filters are pure display, so switching
 * view or toggling a type never re-queries and cannot show a half-loaded month.
 *
 * FIELD WORK (Jeremy, 27 Oct 2026): a field bar names the deployment, its Ajera job and its whole
 * crew, and opens the deployment page, in every view. It carries no status: a deployment is
 * booked when saved, and nobody approves it. The explanation stays off the calendar (9 Oct 2026).
 */
import {
  addDays, dayOfWeek, mdy, mdyRange,
  type CalendarDayCell, type CalendarEntry, type CalendarMonth, type CalendarRow, type PersonRef, type ProjectRef, type RequestType, type WfhDay,
} from "@scheduler/shared";
import { Fragment, useEffect, useMemo, useState, type KeyboardEvent } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { navigate, pathFor, type Route, shareUrl, type View, VIEWS } from "../router";

// "Time Off", not "PTO": every PTO block reads "Time Off" to anyone outside the visibility rule,
// so the filter says the same (9 Oct 2026).
const TYPES: { type: RequestType; label: string }[] = [
  { type: "PTO", label: "Time Off" },
  { type: "NTD", label: "No-travel" },
  { type: "FIELD", label: "Field" },
];

const LABEL: Record<RequestType, string> = { PTO: "Time Off", NTD: "No-travel", FIELD: "Field" };
const ORDER: Record<RequestType, number> = { FIELD: 0, PTO: 1, NTD: 2 };
const FULL_DAY = 8;
/** Blocks shown before a day collapses into "+N more". Four keeps weeks even. */
const MAX_BLOCKS = 4;

const thisMonth = () => new Date().toISOString().slice(0, 7);

/** Today in the browser's own calendar, as YYYY-MM-DD: "Today" means the viewer's today. */
export function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/**
 * The Sunday of the week holding `date`. Weeks run Sunday to Saturday in every view (Jeremy,
 * 21 Oct 2026): Month always did, and Week ran Monday to Sunday until then.
 */
const sundayOf = (date: string) => addDays(date, -dayOfWeek(date));

/**
 * The view this browser last chose (PR 11). A per-viewer convenience, so localStorage, and every
 * read and write survives storage being blocked: the answer is then simply Month.
 */
const VIEW_KEY = "scheduler.calendarView";
function storedView(): View | null {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return VIEWS.find((x) => x === v) ?? null;
  } catch {
    return null;
  }
}
function storeView(view: View): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Blocked storage: the choice lasts for this visit only.
  }
}

/**
 * Whether working from home is drawn (19 Oct 2026): off by default, because it is the busiest and
 * least urgent thing that could be on the calendar. Remembered per browser, like the view.
 */
const WFH_KEY = "scheduler.calendarWfh";
function storedWfh(): boolean {
  try {
    return window.localStorage.getItem(WFH_KEY) === "on";
  } catch {
    return false;
  }
}
function storeWfh(on: boolean): void {
  try {
    window.localStorage.setItem(WFH_KEY, on ? "on" : "off");
  } catch {
    // Blocked storage: the choice lasts for this visit only.
  }
}

/**
 * "Expand days" in Month (punch list S31, S34; 3 Nov 2026): every bar and every working-from-home
 * line on every day, with no "+N more", each week row as tall as its busiest day. Off by default,
 * so a month still fits on a screen. Remembered per browser, like the view.
 */
const EXPAND_KEY = "scheduler.calendarExpand";
function storedExpand(): boolean {
  try {
    return window.localStorage.getItem(EXPAND_KEY) === "on";
  } catch {
    return false;
  }
}
function storeExpand(on: boolean): void {
  try {
    window.localStorage.setItem(EXPAND_KEY, on ? "on" : "off");
  } catch {
    // Blocked storage: the choice lasts for this visit only.
  }
}

/** Working from home by date, for the people on screen; null when the toggle is off. */
type WfhByDate = Map<string, WfhDay[]> | null;

/** Several months of the one payload as one: days in order, each person's entries together. */
function mergeMonths(parts: CalendarMonth[]): CalendarMonth {
  const rows = new Map<string, CalendarRow>();
  for (const part of parts) {
    for (const row of part.rows) {
      const seen = rows.get(row.person.id);
      if (seen) seen.entries.push(...row.entries);
      else rows.set(row.person.id, { person: row.person, entries: [...row.entries] });
    }
  }
  return { month: parts[0]!.month, days: parts.flatMap((p) => p.days), rows: [...rows.values()] };
}

/** Month arithmetic on the string, so no timezone can move it. */
export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const total = y * 12 + (m - 1) + by;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
}

export function monthLabel(month: string): string {
  // date-guard-ok: the month title, "November 2026" -- a month, not a date (F193).
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long", year: "numeric", timeZone: "UTC",
  });
}

/**
 * A Week column heading's date, allowed to break after each "/" (Jeremy, 2 Nov 2026, F193
 * option B): "11/02/2026" is one word to the browser, so a narrow column cut it off; now it
 * drops under the weekday as one piece, and only a narrower column still splits it after a "/".
 * A wide column shows "Mon 11/02/2026" on one line.
 */
function WrapDate({ date }: { date: string }) {
  const parts = mdy(date).split("/");
  return (
    <span className="dow-date">
      {parts.map((p, i) => (
        <Fragment key={i}>
          {p}
          {i < parts.length - 1 ? <>/<wbr /></> : null}
        </Fragment>
      ))}
    </span>
  );
}

const entryOn = (entries: CalendarEntry[], date: string) =>
  entries.find((e) => e.start <= date && e.end >= date);

/** One booking on one day, with everybody it holds. */
interface Block {
  requestId: string;
  type: RequestType;
  /** From the API, already masked: the kind only for a viewer inside the visibility rule. */
  label: string;
  status: "PENDING" | "APPROVED";
  title: string | null;
  people: PersonRef[];
  hours: number | undefined;
  continues: boolean;
  /** FIELD only: the Ajera job. `people` is then the whole crew, from the API. */
  project: ProjectRef | null;
}

/** A field item opens its deployment (27 Oct 2026); `requestId` is the deployment's id. */
const opensDeployment = (block: Pick<Block, "type">) => block.type === "FIELD";
const openDeployment = (id: string) => navigate({ kind: "deployment", id });

/** The props that make a field item a link to its deployment, in any view; nothing for PTO / NTD. */
function linkProps(block: Pick<Block, "type" | "requestId">) {
  if (!opensDeployment(block)) return {};
  return {
    role: "link",
    tabIndex: 0,
    onClick: () => openDeployment(block.requestId),
    // Enter or Space on a focused field item opens it, as a click does.
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDeployment(block.requestId); }
    },
  } as const;
}

/**
 * One booking across one week row: a single bar from its first day in that week to its last
 * (Jeremy, 21 Oct 2026). Until then Month and Week drew a separate piece in every day it touched,
 * so a booking of eight days read as eight bookings.
 *
 * Grouped by REQUEST, not by person: one field deployment with a crew of four is ONE thing
 * happening, and four bars would read as four deployments. That is still the reason Month is not
 * just the grid transposed.
 */
interface Bar {
  block: Block;
  /** 0-based column of its first day in the row, and how many columns it covers. */
  col: number;
  span: number;
  /** It began before this row (an earlier week or month): its left end is squared off. */
  fromBefore: boolean;
  /** It carries on after this row: its right end is squared off. */
  toAfter: boolean;
  /** Which stacked line of the row it sits on, 0 first. */
  lane: number;
}

/**
 * Lay out a week's bookings as bars. `week` is the row's seven dates, null for a padding cell
 * outside the month. Bars are clipped to the row's real dates; a longer bar takes the first lane
 * that is free across every column it covers, so two bookings never overlap and a long one is not
 * broken up to fit around a short one.
 */
function barsForWeek(rows: CalendarRow[], week: readonly (string | null)[]): Bar[] {
  const found = new Map<string, { block: Block; first: number; last: number; fullStart: string; fullEnd: string; hours: Record<string, number> }>();
  for (const row of rows) {
    for (const e of row.entries) {
      let first = -1;
      let last = -1;
      week.forEach((d, i) => {
        if (d !== null && e.start <= d && e.end >= d) {
          if (first < 0) first = i;
          last = i;
        }
      });
      if (first < 0) continue;
      const seen = found.get(e.requestId);
      if (seen) {
        // The same request again: another crew member, or the other month of a week that
        // crosses two (both months' entries arrive for the one request).
        seen.first = Math.min(seen.first, first);
        seen.last = Math.max(seen.last, last);
        // Field work names its whole crew from the API already; PTO and NTD gather their person.
        if (!e.crew && !seen.block.people.some((p) => p.id === row.person.id)) seen.block.people.push(row.person);
        Object.assign(seen.hours, e.hoursByDate);
        continue;
      }
      found.set(e.requestId, {
        block: {
          requestId: e.requestId, type: e.type, label: e.label, status: e.status, title: e.title,
          people: e.crew ? [...e.crew] : [row.person], hours: undefined, continues: false, project: e.project,
        },
        first, last, fullStart: e.fullStart, fullEnd: e.fullEnd, hours: { ...e.hoursByDate },
      });
    }
  }

  const bars: Bar[] = [...found.values()].map((f) => {
    const firstDate = week[f.first]!;
    const lastDate = week[f.last]!;
    const fromBefore = f.fullStart < firstDate;
    const toAfter = f.fullEnd > lastDate;
    // Hours only mean something on a one-day bar ("Ana Diaz — PTO 4h"); a longer one can mix them.
    const hours = f.first === f.last ? f.hours[firstDate] : undefined;
    return {
      block: { ...f.block, hours, continues: fromBefore || toAfter },
      col: f.first, span: f.last - f.first + 1, fromBefore, toAfter, lane: 0,
    };
  });

  // Longest first within a start column, so long bookings keep a steady line across the row.
  bars.sort((a, b) =>
    a.col - b.col || b.span - a.span || ORDER[a.block.type] - ORDER[b.block.type] ||
    a.block.people[0]!.name.localeCompare(b.block.people[0]!.name));
  const taken: boolean[][] = [];
  for (const bar of bars) {
    let lane = 0;
    for (;;) {
      const line = (taken[lane] ??= Array<boolean>(week.length).fill(false));
      let free = true;
      for (let c = bar.col; c < bar.col + bar.span; c++) if (line[c]) free = false;
      if (free) {
        for (let c = bar.col; c < bar.col + bar.span; c++) line[c] = true;
        bar.lane = lane;
        break;
      }
      lane += 1;
    }
  }
  return bars;
}

const crewNames = (block: Block) => block.people.map((p) => p.name).join(", ");

/**
 * "Waters delineation · Monte Alto Wind · Ana Diaz, Ben Ruiz" for field work: the deployment, the
 * job and every name, which a narrow Month bar cuts off and the hover gives whole. "Ana Diaz — PTO
 * 4h" for the rest.
 */
function blockLabel(block: Block): string {
  if (block.type === "FIELD") {
    return [block.title ?? LABEL.FIELD, block.project?.name, crewNames(block)].filter(Boolean).join(" · ");
  }
  const who =
    block.people.length === 1
      ? block.people[0]!.name
      : `${block.people[0]!.name} +${block.people.length - 1}`;
  const hours = block.hours !== undefined && block.hours < FULL_DAY ? ` ${block.hours}h` : "";
  return `${who} — ${block.label}${hours}`;
}

/**
 * The hover text. Field work says what it is, the job and the whole crew, and never a status: it
 * is booked when saved (27 Oct 2026). PTO and NTD still say pending or approved, since they are.
 */
function blockTitle(block: Block): string {
  if (block.type === "FIELD") {
    const parts = [`Field work: ${block.title ?? LABEL.FIELD}`];
    if (block.project) parts.push(`Job: ${block.project.name}`);
    parts.push(`Crew: ${crewNames(block)}`);
    if (block.continues) parts.push("Runs beyond this day");
    parts.push("Click to open");
    return parts.join("\n");
  }
  const parts = [block.label, crewNames(block)];
  if (block.hours !== undefined && block.hours < FULL_DAY) parts.push(`${block.hours}h`);
  parts.push(block.status === "PENDING" ? "pending" : "approved");
  if (block.title) parts.push(block.title);
  if (block.continues) parts.push("runs beyond this day");
  return parts.join(" — ");
}

export function TeamCalendar({ route }: { route: Extract<Route, { kind: "calendar" }> }) {
  const api = useSchedulerApi();

  // Month, date and view live in the URL, so "the calendar for November" and "the week of the
  // 2nd" are things you can send to somebody. A null month or date means "now", and stays null in
  // the address rather than pinning today into a link that should keep meaning "current". With no
  // view in the address, the view this browser last chose; Month the first time (PR 11).
  const view: View = route.view ?? (route.month ? "month" : route.date ? "day" : storedView() ?? "month");
  const today = localToday();
  const date = route.date ?? today;
  const month = route.month ?? (view === "day" || view === "week" ? date.slice(0, 7) : thisMonth());
  const weekStart = sundayOf(date);
  const weekEnd = addDays(weekStart, 6);
  const first = view === "week" ? weekStart : view === "day" ? date : `${month}-01`;
  const last = view === "week" ? weekEnd : view === "day" ? date : null;
  // A week across two months reads both: the same call, twice, nothing new.
  const months = view === "week" ? [...new Set([weekStart.slice(0, 7), weekEnd.slice(0, 7)])] : [month];
  const monthsKey = months.join(",");

  const setMonth = (next: string) => navigate({ kind: "calendar", month: next, date: null, view });
  const setDate = (next: string | null) => navigate({ kind: "calendar", month: null, date: next, view });
  // replace, not push: flipping the view is not somewhere you went, and Back
  // should leave the calendar rather than walk through every toggle. The day or month shown
  // carries across: a month that is not this one opens Day and Week on its 1st.
  const setView = (next: View) => {
    storeView(next);
    const byDate = next === "day" || next === "week";
    const wasByDate = view === "day" || view === "week";
    const nextDate = wasByDate ? route.date : route.month && route.month !== thisMonth() ? `${route.month}-01` : null;
    const nextMonth = wasByDate ? (route.date ? route.date.slice(0, 7) : null) : route.month;
    navigate({ kind: "calendar", month: byDate ? null : nextMonth, date: byDate ? nextDate : null, view: next }, true);
  };
  const [data, setData] = useState<CalendarMonth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [shown, setShown] = useState<Record<RequestType, boolean>>({ PTO: true, NTD: true, FIELD: true });
  const [activeOnly, setActiveOnly] = useState(true);
  const [busyOnly, setBusyOnly] = useState(false);
  const [find, setFind] = useState("");
  const [showWfh, setShowWfh] = useState(storedWfh);
  const [expand, setExpand] = useState(storedExpand);
  const [wfh, setWfh] = useState<WfhDay[]>([]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  // Fetched only while the toggle is on: the same months as the calendar, from /api/wfh.
  useEffect(() => {
    if (!showWfh) { setWfh([]); return; }
    let current = true;
    Promise.all(monthsKey.split(",").map((m) => api.wfhMonth(m)))
      .then((parts) => { if (current) setWfh(parts.flatMap((p) => p.entries)); })
      .catch((e: unknown) => { if (current) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { current = false; };
  }, [api, monthsKey, showWfh, back]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    Promise.all(monthsKey.split(",").map((m) => api.calendar(m, activeOnly)))
      .then((parts) => {
        // A slow first fetch must not overwrite a faster second one.
        if (current) { setData(mergeMonths(parts)); setError(null); }
      })
      .catch((e: unknown) => {
        if (current) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [api, monthsKey, activeOnly, back]);

  /** Filters applied once; both views read the result. */
  const rows = useMemo(() => {
    if (!data) return [];
    const needle = find.trim().toLowerCase();
    const homeworkers = new Set(showWfh ? wfh.map((w) => w.person.id) : []);
    return data.rows
      .map((row) => ({ ...row, entries: row.entries.filter((e) => shown[e.type]) }))
      .filter((row) => (needle === "" ? true : row.person.name.toLowerCase().includes(needle)))
      .filter((row) => (busyOnly ? row.entries.length > 0 || homeworkers.has(row.person.id) : true));
  }, [data, shown, busyOnly, find, showWfh, wfh]);

  /** Working from home for the people the filters left on screen, by date. */
  const wfhByDate: WfhByDate = useMemo(() => {
    if (!showWfh) return null;
    const onScreen = new Set(rows.map((r) => r.person.id));
    const out = new Map<string, WfhDay[]>();
    for (const w of wfh) {
      if (!onScreen.has(w.person.id)) continue;
      out.set(w.date, [...(out.get(w.date) ?? []), w]);
    }
    return out;
  }, [showWfh, wfh, rows]);
  const wfhCount = wfh.filter((w) => w.date >= first && (last === null || w.date <= last)).length;

  // Counted over what is on screen: the day, the week or the month.
  const counts = useMemo(() => {
    const out: Record<RequestType, number> = { PTO: 0, NTD: 0, FIELD: 0 };
    const seen = new Set<string>();
    for (const row of data?.rows ?? []) {
      for (const e of row.entries) {
        if (e.end < first || (last !== null && e.start > last)) continue;
        // Distinct requests: a crew of four is one thing happening, not four.
        if (seen.has(e.requestId)) continue;
        seen.add(e.requestId);
        out[e.type] += 1;
      }
    }
    return out;
  }, [data, first, last]);

  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <div className="calbar">
        <div className="calnav">
          {view === "day" ? (
            <>
              <button type="button" className="ghost" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}>← Prev</button>
              <h2>{dayLabel(date)}</h2>
              <button type="button" className="ghost" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}>Next →</button>
              {date !== today ? <button type="button" className="ghost" onClick={() => setDate(null)}>Today</button> : null}
            </>
          ) : view === "week" ? (
            <>
              <button type="button" className="ghost" aria-label="Previous week" onClick={() => setDate(addDays(weekStart, -7))}>← Prev</button>
              <h2>{weekLabel(weekStart, weekEnd)}</h2>
              <button type="button" className="ghost" aria-label="Next week" onClick={() => setDate(addDays(weekStart, 7))}>Next →</button>
              {weekStart !== sundayOf(today) ? <button type="button" className="ghost" onClick={() => setDate(null)}>This week</button> : null}
            </>
          ) : (
            <>
              <button type="button" className="ghost" onClick={() => setMonth(shiftMonth(month, -1))}>← Prev</button>
              <h2>{monthLabel(month)}</h2>
              <button type="button" className="ghost" onClick={() => setMonth(shiftMonth(month, 1))}>Next →</button>
              {month !== thisMonth() ? (
                <button type="button" className="ghost" onClick={() => navigate({ kind: "calendar", month: null, date: null, view })}>Today</button>
              ) : null}
            </>
          )}
          <div className="viewpick" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button
                key={v} type="button" className={`seg${view === v ? " on" : ""}`}
                aria-pressed={view === v} onClick={() => setView(v)} title={v === "grid" ? "Resource grid: one row per person" : undefined}
              >
                {VIEW_LABEL[v]}
              </button>
            ))}
          </div>
          <CopyLink url={shareUrl(view === "day" || view === "week"
            ? { kind: "calendar", month: null, date: view === "week" ? weekStart : date, view }
            : { kind: "calendar", month, date: null, view })} />
        </div>

        <div className="calfilters">
          {TYPES.map(({ type, label }) => (
            <button
              key={type} type="button"
              className={`chip t-${type}${shown[type] ? " on" : ""}`}
              aria-pressed={shown[type]}
              onClick={() => setShown((s) => ({ ...s, [type]: !s[type] }))}
            >
              <i />{label}<span className="n">{counts[type]}</span>
            </button>
          ))}
          <button
            type="button" className={`chip t-WFH${showWfh ? " on" : ""}`} aria-pressed={showWfh}
            onClick={() => setShowWfh((on) => { storeWfh(!on); return !on; })}
          >
            <i />Working from home{showWfh ? <span className="n">{wfhCount}</span> : null}
          </button>
          {view === "grid" ? (
            <label className="chip switch">
              <input type="checkbox" checked={busyOnly} onChange={(e) => setBusyOnly(e.target.checked)} />
              Only people with something on
            </label>
          ) : null}
          <label className="chip switch">
            <input type="checkbox" checked={!activeOnly} onChange={(e) => setActiveOnly(!e.target.checked)} />
            Include inactive
          </label>
          {view === "month" ? (
            <label className="chip switch">
              <input
                type="checkbox" checked={expand}
                onChange={(e) => { const on = e.target.checked; storeExpand(on); setExpand(on); }}
              />
              Expand days
            </label>
          ) : null}
          <input
            className="calfind" type="search" value={find} placeholder="Find a person…"
            aria-label="Find a person" onChange={(e) => setFind(e.target.value)}
          />
        </div>
      </div>

      {data === null && loading ? <p className="lede">Loading…</p> : null}

      {data !== null ? (
        view === "day"
          ? <DayView rows={rows} day={data.days.find((d) => d.date === date) ?? null} date={date} wfh={wfhByDate} />
          : view === "week"
            ? <WeekView rows={rows} days={data.days.filter((d) => d.date >= weekStart && d.date <= weekEnd)} today={today} wfh={wfhByDate} />
            : view === "month"
              ? <MonthView data={data} rows={rows} wfh={wfhByDate} expand={expand} />
              : <ResourceGrid data={data} rows={rows} wfh={wfhByDate} />
      ) : null}
    </>
  );
}

/**
 * One week of the calendar as a row: the day cells behind, the bookings as bars across them, and
 * under the bars, per day, "+N more" and working from home. Month draws one per week with at most
 * `maxLanes` lines of bars; Week draws one with every booking.
 *
 * One CSS grid per row. Each day's background spans every grid row, and the 1px column gaps over
 * the row's border-coloured fill are the lines between days; a bar spanning columns covers the
 * gaps between them, which is what makes it one solid bar.
 */
function WeekRow({ days, rows, today, wfh, maxLanes, maxWfh, showDayNumber, opensDay, tall }: {
  days: readonly (CalendarDayCell | null)[];
  rows: CalendarRow[];
  today: string;
  wfh: WfhByDate;
  maxLanes: number;
  maxWfh?: number;
  showDayNumber: boolean;
  opensDay?: boolean;
  tall?: boolean;
}) {
  const week = days.map((d) => d?.date ?? null);
  const bars = useMemo(() => barsForWeek(rows, week), [rows, week.join(",")]);
  const lanes = bars.reduce((n, b) => Math.max(n, b.lane + 1), 0);
  const shownLanes = Math.min(lanes, maxLanes);
  const hiddenOn = (col: number) =>
    bars.filter((b) => b.lane >= shownLanes && col >= b.col && col < b.col + b.span);
  // No lanes means NO repeat(): `repeat(0, auto)` is invalid CSS, so the browser drops the whole
  // value and a week with no bookings drew its backgrounds over the day-number strip only, with
  // its working-from-home lines on the row's border colour below (punch list S3, 28 Oct 2026).
  const laneRows = shownLanes > 0 ? ` repeat(${shownLanes}, auto)` : "";
  return (
    <div
      className={`calrow${tall ? " tall" : ""}`}
      style={{ gridTemplateRows: `auto${laneRows} 1fr` }}
    >
      {days.map((day, i) => (
        <div
          key={`bg-${i}`}
          className={`cellbg${!day ? " pad" : ""}${day?.weekend ? " off" : ""}${day?.holiday || (day && blackoutOn(day)) ? " hol" : ""}${day?.date === today ? " today" : ""}`}
          style={{ gridColumn: i + 1, gridRow: "1 / -1" }}
        />
      ))}
      {/* A head on every real day, even an empty one in Week, so the bars never sit on the top edge.
          In Month the head is a shaded strip that opens that day in Day view (S31, S34). */}
      {days.map((day, i) => {
        if (!day) return null;
        const className = `cell-head${day.date === today ? " today" : ""}${opensDay ? " opens-day" : ""}`;
        const style = { gridColumn: i + 1, gridRow: 1 };
        const content = (
          <>
            {showDayNumber ? <span className="n">{day.day}</span> : null}
            {day.holiday ? <span className="holname" title={day.holiday}>{day.holiday}</span> : null}
            <EventNames day={day} />
          </>
        );
        if (!opensDay) return <div key={`h-${day.date}`} className={className} style={style}>{content}</div>;
        const route = { kind: "calendar", month: null, date: day.date, view: "day" } as const;
        return (
          <a
            key={`h-${day.date}`} className={className} style={style} href={pathFor(route)}
            onClick={(e) => { e.preventDefault(); navigate(route); }}
          >
            {content}
          </a>
        );
      })}
      {bars.filter((b) => b.lane < shownLanes).map((b) => (
        <div
          key={b.block.requestId}
          className={`block bar b-${b.block.type}${b.block.status === "PENDING" ? " pend" : ""}${b.fromBefore ? " from-before" : ""}${b.toAfter ? " to-after" : ""}${opensDeployment(b.block) ? " opens" : ""}`}
          style={{ gridColumn: `${b.col + 1} / span ${b.span}`, gridRow: b.lane + 2 }}
          title={blockTitle(b.block)}
          {...linkProps(b.block)}
        >
          {b.fromBefore ? <span className="cont" aria-hidden="true">◂ </span> : null}
          {b.block.status === "PENDING" ? <span className="pendtag">Pending · </span> : null}
          {blockLabel(b.block)}
        </div>
      ))}
      {days.map((day, i) => {
        if (!day) return null;
        const hidden = hiddenOn(i);
        const home = wfh?.get(day.date);
        if (hidden.length === 0 && !home?.length) return null;
        return (
          <div key={`f-${day.date}`} className="cellfoot" style={{ gridColumn: i + 1, gridRow: shownLanes + 2 }}>
            {hidden.length > 0 ? (
              <div className="more" title={hidden.map((b) => blockTitle(b.block)).join("\n")}>+{hidden.length} more</div>
            ) : null}
            <WfhLines list={home} max={maxWfh} />
          </div>
        );
      })}
    </div>
  );
}

/** Weeks as rows, Sunday first, with only what is booked drawn: each booking one bar per week. */
function MonthView({ data, rows, wfh, expand }: { data: CalendarMonth; rows: CalendarRow[]; wfh: WfhByDate; expand: boolean }) {
  const today = new Date().toISOString().slice(0, 10);

  // Pad to whole weeks. The blanks are real cells, not styling: without them the
  // 1st of a month falls under the wrong weekday and every date after it lies.
  const lead = data.days[0]?.weekday ?? 0;
  const cells: (CalendarMonth["days"][number] | null)[] = [
    ...Array.from<null>({ length: lead }).fill(null),
    ...data.days,
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (typeof cells)[] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <>
      <div className="calgrid">
        <div className="caldow">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="dow">{d}</div>
          ))}
        </div>
        {weeks.map((week, i) => (
          <WeekRow
            key={i} days={week} rows={rows} today={today} wfh={wfh}
            maxLanes={expand ? Number.POSITIVE_INFINITY : MAX_BLOCKS} maxWfh={expand ? undefined : MAX_WFH}
            showDayNumber opensDay
          />
        ))}
      </div>

      <Legend wfh={wfh !== null} />
      <p className="lede" style={{ marginTop: 14 }}>
        Only what is booked is drawn, so an empty day is genuinely empty. Each booking is one bar
        across its days, and a field deployment appears once with its job and its whole crew rather
        than once per person — it is one thing happening. Hover a bar for the details; click a field
        work bar to open the deployment.
      </p>
    </>
  );
}

const VIEW_LABEL: Record<View, string> = { day: "Day", week: "Week", month: "Month", grid: "Grid" };

/** "Mon 11/02/2026". */
const dayLabel = (date: string) => `${"SunMonTueWedThuFriSat".slice(dayOfWeek(date) * 3, dayOfWeek(date) * 3 + 3)} ${mdy(date)}`;

/** "11/01/2026 – 11/07/2026". */
const weekLabel = (start: string, end: string) => mdyRange(start, end);

/** Sunday to Saturday, one column per day, every booking shown: a week has the room a month has not. */
function WeekView({ rows, days, today, wfh }: { rows: CalendarRow[]; days: CalendarDayCell[]; today: string; wfh: WfhByDate }) {
  return (
    <>
      <div className="calgrid calweek">
        <div className="caldow">
          {days.map((d) => (
            <div key={`h-${d.date}`} className="dow">{"SunMonTueWedThuFriSat".slice(d.weekday * 3, d.weekday * 3 + 3)} <WrapDate date={d.date} /></div>
          ))}
        </div>
        <WeekRow days={days} rows={rows} today={today} wfh={wfh} maxLanes={Number.POSITIVE_INFINITY} showDayNumber={false} tall />
      </div>
      <Legend wfh={wfh !== null} />
    </>
  );
}

const DAY_GROUPS: { type: RequestType; heading: string }[] = [
  { type: "PTO", heading: "Time Off" },
  { type: "NTD", heading: "No travel" },
  { type: "FIELD", heading: "Field work" },
];

/**
 * One date: everyone out, grouped by type, one line per person. The label is the API's, so the
 * kind shows only to a viewer inside the visibility rule and "Time Off" to everyone else, exactly
 * as Month draws it.
 */
function DayView({ rows, day, date, wfh }: { rows: CalendarRow[]; day: CalendarDayCell | null; date: string; wfh: WfhByDate }) {
  const lines = rows
    .map((row) => ({ person: row.person, hit: entryOn(row.entries, date) }))
    .filter((x): x is { person: PersonRef; hit: CalendarEntry } => x.hit !== undefined);
  // Field work is one line per DEPLOYMENT (27 Oct 2026), naming its job and whole crew, and opens it.
  const trips = [...new Map(lines.filter((l) => l.hit.type === "FIELD").map((l) => [l.hit.requestId, l.hit])).values()]
    .sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "") || a.requestId.localeCompare(b.requestId));
  return (
    <>
      {day?.holiday ? <p className="flag">Company holiday: {day.holiday}</p> : day?.weekend ? <p className="muted">A weekend day.</p> : null}
      {/* Company events (S26): just each name, a blackout as the holiday flag, information only muted. */}
      {day?.events.map((e, i) => <p key={i} className={e.kind === "BLACKOUT" ? "flag" : "muted"}>{e.name}</p>)}
      {lines.length === 0 ? <div className="note">Nobody is out.</div> : (
        <div className="dayview">
          {DAY_GROUPS.map(({ type, heading }) => {
            const group = lines.filter((l) => l.hit.type === type).sort((a, b) => a.person.name.localeCompare(b.person.name));
            if (!group.length) return null;
            if (type === "FIELD") {
              return (
                <section key={type} className="daygroup" aria-label={heading}>
                  <h3>{heading} <span className="muted">{group.length}</span></h3>
                  {trips.map((hit) => (
                    <div key={hit.requestId} className="block dayline b-FIELD opens" title={`Open ${hit.title ?? LABEL.FIELD}`} {...linkProps(hit)}>
                      <strong>{hit.title ?? LABEL.FIELD}</strong>
                      {hit.project ? ` — ${hit.project.name}` : ""}
                      {` — ${(hit.crew ?? []).map((p) => p.name).join(", ")}`}
                    </div>
                  ))}
                </section>
              );
            }
            return (
              <section key={type} className="daygroup" aria-label={heading}>
                <h3>{heading} <span className="muted">{group.length}</span></h3>
                {group.map(({ person, hit }) => {
                  const h = hit.hoursByDate[date];
                  const what = type === "NTD" ? null : hit.label;
                  return (
                    <div key={`${hit.requestId}-${person.id}`} className={`block dayline b-${type}${hit.status === "PENDING" ? " pend" : ""}`}>
                      {hit.status === "PENDING" ? <span className="pendtag">Pending · </span> : null}
                      <strong>{person.name}</strong>
                      {what ? ` — ${what}` : ""}
                      {h !== undefined && h < FULL_DAY ? ` · ${h} h (part day)` : ""}
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}
      {/* Not "out": somebody working from home is working. Its own heading, after the rest. */}
      {wfh ? (
        <section className="daygroup" aria-label="Working from home">
          <h3>Working from home <span className="muted">{wfh.get(date)?.length ?? 0}</span></h3>
          {(wfh.get(date) ?? []).length === 0 ? <p className="muted">Nobody.</p> : null}
          {(wfh.get(date) ?? []).map((w) => (
            <div key={w.person.id} className="wfhline dayline" title={wfhTitle(w)}>
              <House /><strong>{w.person.name}</strong>{w.portion === "HALF" ? " · ½ day" : ""}
            </div>
          ))}
        </section>
      ) : null}
      <Legend wfh={wfh !== null} />
    </>
  );
}

/** One row per person, one cell per day. The planning view. */
function ResourceGrid({ data, rows, wfh }: { data: CalendarMonth; rows: CalendarRow[]; wfh: WfhByDate }) {
  return (
    <>
      <div className="gridwrap">
        <table className="sched">
          <thead>
            <tr>
              <th className="who">Resource</th>
              {data.days.map((d) => (
                <th key={d.date} className={`d${d.weekend || d.holiday || blackoutOn(d) ? " off" : ""}`} title={dayTitle(d)} scope="col">
                  {d.day}<span>{"SMTWTFS"[d.weekday]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.person.id}>
                <th className="who" scope="row">{row.person.name}</th>
                {data.days.map((d) => {
                  const hit = entryOn(row.entries, d.date);
                  const home = wfh?.get(d.date)?.find((w) => w.person.id === row.person.id);
                  // Drawn inside the cell, so a booking and a WFH mark on the same day both show.
                  const mark = home ? <span className="wfhmark" title={wfhTitle(home)}><House />{home.portion === "HALF" ? "½" : null}</span> : null;
                  if (!hit) {
                    return <td key={d.date} className={d.holiday || blackoutOn(d) ? "hol" : d.weekend ? "off" : ""} title={home ? wfhTitle(home) : dayTitle(d)}>{mark}</td>;
                  }
                  const hours = hit.hoursByDate[d.date];
                  const partial = hours !== undefined && hours < FULL_DAY;
                  return (
                    <td
                      key={d.date}
                      className={`f-${hit.type}${hit.status === "PENDING" ? " pend" : ""}${partial ? " half" : ""}${opensDeployment(hit) ? " opens" : ""}`}
                      title={blockTitle({
                        requestId: hit.requestId, type: hit.type, label: hit.label, status: hit.status, title: hit.title,
                        people: hit.crew ?? [row.person], hours, continues: hit.fullStart < d.date || hit.fullEnd > d.date,
                        project: hit.project,
                      }) + (home ? `\n${wfhTitle(home)}` : "")}
                      {...linkProps(hit)}
                    >
                      {mark}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 ? (
        <p className="lede" style={{ marginTop: 14 }}>
          {data.rows.length === 0 ? "No people in the roster yet." : "Nobody matches those filters."}
        </p>
      ) : null}

      <Legend partial wfh={wfh !== null} />
      <p className="lede" style={{ marginTop: 14 }}>
        One row per person, one cell per day — so a double-booking is visible rather than something
        you find out about on the morning of a deployment. An empty row means that person is free all
        month. Balances are not shown here; they are a different question.
      </p>
    </>
  );
}

/** WFH lines show at most this many in a Month cell; the rest are named on hover. */
const MAX_WFH = 3;

const wfhTitle = (w: WfhDay) => `${w.person.name} — working from home${w.portion === "HALF" ? ", half day" : ""}`;

/** A small house: the working-from-home mark, the same in every view. */
function House() {
  return (
    <svg className="house" viewBox="0 0 12 12" width="11" height="11" aria-hidden="true" focusable="false">
      <path d="M1.5 6 6 2l4.5 4M3 5v5h6V5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

/** Working from home in a Month or Week cell: light, after the bookings, "½" where half. */
function WfhLines({ list, max }: { list: WfhDay[] | undefined; max?: number }) {
  if (!list?.length) return null;
  const shown = max === undefined ? list : list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <>
      {shown.map((w) => (
        <div key={w.person.id} className="wfhline" title={wfhTitle(w)}>
          <House />{w.person.name}{w.portion === "HALF" ? " ½" : ""}
        </div>
      ))}
      {extra > 0 ? <div className="more wfhmore" title={list.slice(shown.length).map(wfhTitle).join("\n")}>+{extra} working from home</div> : null}
    </>
  );
}

/** A company BLACKOUT on this day (S26): drawn with the holiday's background. */
const blackoutOn = (d: CalendarDayCell) => d.events.some((e) => e.kind === "BLACKOUT");

/** The holiday and each company event on this day, one per line, for a hover; undefined when none. */
const dayTitle = (d: CalendarDayCell) => [d.holiday, ...d.events.map((e) => e.name)].filter(Boolean).join("\n") || undefined;

/**
 * Each company event on a day, by name and nothing else (Jeremy, 1 Nov 2026): a blackout in the
 * holiday's colour, information only quieter, so the two read apart.
 */
function EventNames({ day }: { day: CalendarDayCell }) {
  return (
    <>
      {day.events.map((e, i) => (
        <span key={i} className={`holname${e.kind === "INFO" ? " evinfo" : ""}`} title={e.name}>{e.name}</span>
      ))}
    </>
  );
}

function Legend({ partial, wfh }: { partial?: boolean; wfh?: boolean }) {
  return (
    <div className="callegend">
      <span><i className="f-PTO" />Time Off</span>
      <span><i className="f-NTD" />No-travel</span>
      <span><i className="f-FIELD" />Field</span>
      {wfh ? <span><i className="wfh" />Working from home</span> : null}
      {partial ? <span><i className="f-PTO half" />Partial day</span> : null}
      <span><i className="hol" />Holiday</span>
      <span><i className="f-PTO pend" />Pending</span>
    </div>
  );
}

/**
 * The address of exactly what is on screen, ready to paste.
 *
 * Copies the RESOLVED month rather than the "current month" form: a link sent
 * to somebody saying "look at November" must still mean November when they
 * open it in December.
 *
 * `navigator.clipboard` needs a secure context and permission, and refuses in
 * places the demo actually runs, so there is a fallback rather than a button
 * that silently does nothing.
 */
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const field = document.createElement("textarea");
      field.value = url;
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(field);
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" className="ghost" onClick={() => void copy()} title={url}>
      {copied ? "✓ Link copied" : "Copy link"}
    </button>
  );
}
